import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter, Logger, type Event } from '@patsnap/tahiti-core';
import type { PatentProfileWithMatchedTechFeature, RetrievalResult, TechFeatureSynonym } from '../../types';
import { FtoReportComplexRetrievalRepository, type FtoReportComplexRetrievalService, type RetrievalTimeCost } from '../complex-retrieval-service';
import { FtoReportRetrievalProgressService } from '../retrieval-progress-service';
import { FtoReportCurrentMatchedPatentModel } from '../../models/current-matched-patent-model';
import _ from 'lodash';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import type { RETRIEVAL_STRATEGY } from '../../constants';
import { FtoReportTerminateSearchAgentApi } from '../../apis/terminate-search-agent-api';

@injectable()
export class FtoReportComplexRetrievalServiceImpl implements FtoReportComplexRetrievalService {

    // eslint-disable-next-line max-params
    constructor(
        @inject(Logger)
        private readonly _logger: Logger,
        @inject(FtoReportRetrievalProgressService)
        private readonly _retrievalProgressService: FtoReportRetrievalProgressService,
        @inject(FtoReportCurrentMatchedPatentModel)
        private readonly _currentMatchedPatentModel: FtoReportCurrentMatchedPatentModel,
        @inject(FtoReportComplexRetrievalRepository)
        private readonly _complexRetrievalRepository: FtoReportComplexRetrievalRepository,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(FtoReportTerminateSearchAgentApi)
        private readonly _terminateSearchAgentApi: FtoReportTerminateSearchAgentApi,
    ) { }

    private _onRetrievalStartEmitter: Emitter<void> = new Emitter();
    private _onRetrievalRunningEmitter: Emitter<[RETRIEVAL_STRATEGY, boolean]> = new Emitter();
    private _onRetrievalCompleteEmitter: Emitter<void> = new Emitter();
    private _onPatentFeatureMatchedEmitter: Emitter<PatentProfileWithMatchedTechFeature[]> = new Emitter();
    private _onTechFeatureSynonymEmitter: Emitter<TechFeatureSynonym[]> = new Emitter();
    private _onRetrievalTimeEmitter: Emitter<RetrievalTimeCost> = new Emitter();

    readonly onRetrievalStart: Event<void> = this._onRetrievalStartEmitter.event;
    readonly onRetrievalRunning: Event<[RETRIEVAL_STRATEGY, boolean]> = this._onRetrievalRunningEmitter.event;
    readonly onRetrievalComplete: Event<void> = this._onRetrievalCompleteEmitter.event;
    readonly onPatentFeatureMatched: Event<PatentProfileWithMatchedTechFeature[]> = this._onPatentFeatureMatchedEmitter.event;
    readonly onTechFeatureSynonym: Event<TechFeatureSynonym[]> = this._onTechFeatureSynonymEmitter.event;
    readonly onRetrievalTime: Event<RetrievalTimeCost> = this._onRetrievalTimeEmitter.event;

    async terminate(): Promise<void> {
        // 触发检索完成事件
        this._onRetrievalCompleteEmitter.fire();
        // 终止当前的检索（循环）
        await this._complexRetrievalRepository.terminate();
        this._retrievalProgressService.setAllResultFinished();
        // 将检索中的策略进度设置为 false
        this._setRetrievalProgressToFalse();
        // 发送终止搜索请求的请求
        return this._terminateRepository();
    }

    // 将检索中的策略进度设置为 false
    private _setRetrievalProgressToFalse() {
        const runningStrategies = this._retrievalProgressService.getRunningStrategies();
        runningStrategies.forEach((strategy) => {
            this._retrievalProgressService.add(strategy, false);
        });
    }

    private _terminateRepository() {
        return this._terminateSearchAgentApi.terminateSearchAgent({
            task_id: this._currentTaskService.taskId,
        });
    }

    async search(): Promise<RetrievalResult> {
        const taskId = this._currentTaskService.taskId;
        this._logger.info('复杂检索服务开始检索', taskId);
        this._onRetrievalStartEmitter.fire();
        await this._complexRetrievalRepository.search(taskId, {
            onRetrievalRunning: (name, running) => this._retrievalProgressService.add(name, running),
            onRetrievalSuccess: (name, result) => this._retrievalProgressService.updateResult(name, result),
            onPatentFeatureMatched: (patents) => this._firePatentFeatureMatched(patents),
            onTechFeatureSynonym: (synonyms) => this._onTechFeatureSynonymEmitter.fire(synonyms),
            onRetrievalTime: (time) => this._onRetrievalTimeEmitter.fire(time),
        });
        this._onRetrievalCompleteEmitter.fire();
        return {
            idea: '',
            searchExpress: '',
            patents: [],
            strategyParameters: [],
            nextPlan: '',
            finished: false,
        };
    }

    /**
     * 触发专利有匹配特征事件
     * @param techFeatures 匹配特征
     */
    private _firePatentFeatureMatched(techFeatures: PatentProfileWithMatchedTechFeature[]) {
        this._onPatentFeatureMatchedEmitter.fire(techFeatures);
        this._currentMatchedPatentModel.update((state) => ({
            ...state,
            ranking: _.uniqBy([...techFeatures], 'patentId'),
        }));
    }
}
